import moment from 'moment';
import React from 'react';
import _camelCase from 'lodash/camelCase';

// Convert snake_case string to Capitalized Words
export const snakeToCaps = value => (
  value && value
    .replace(/\./g, ':_') // Underscore will be rewritten to space
    .split('_')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ')
);

// Convert snake_case string to camelCase string
export const snakeToCamel = value => value && _camelCase(value);

// Convert any string to camel case (same as the function above, but with a wider name)
// This should be removed in favor of @usealloy/generic-utils#caseFns once installed in Daffy
export const toCamel = value => value && typeof value === 'string' && _camelCase(value);


// Convert kebab-case string to Capitalized Words
export const kebabToCaps = value => (
  value && value
    .replace(/\./g, ':-') // Underscore will be rewritten to space
    .split('-')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ')
);

// Convert snake_case string to lowercase words
export const snakeToLower = value => (
  value && value.split('_').map(word => word.toLowerCase()).join(' ')
);

// Convert ALL CAPS string to Capitalized Words
export const capsToNormal = value => (
  value && value
    .split(' ')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(' ')
);

// Convert ALL camelCase string to Capitalized Words
export const camelToNormal = value => (
  value && value
    .replace(/([A-Z])/g, ' $1')
    .split(' ')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(' ')
);

// Format timestamps
export const DEFAULT_MOMENT_FORMAT = 'MMM D, YYYY, h:mm A';

export const formatTimestamp = (timestamp, format = DEFAULT_MOMENT_FORMAT) => (
  moment.utc(timestamp).local().format(format)
);

export const formatTimestampUtc = (timestamp, format = DEFAULT_MOMENT_FORMAT) => (
  moment.utc(timestamp).utcOffset(0).format(format)
);

// format numeric-string in format XXXXXXXXXX to XXX-XXX-XXXX format
export const formatPhoneNumber = value => {
  if (value && value.length === 10) {
    return value.replace(/^\(?([0-9]{3})\)?[-. ]?([0-9]{3})[-. ]?([0-9]{4})$/, '$1-$2-$3');
  }
  return value;
};

// For formatting dollar values, which is represented in dollar amounts
// If value is NaN / non parseable, return empty string
export const formatDollarValues = value => {
  const parsedFloat = parseFloat(value);
  if (isNaN(parsedFloat)) {
    return '';
  }
  const dollarAmount = parsedFloat.toFixed(0).replace(/(\d)(?=(\d{3})+(?!\d))/g, '$1,');
  // Regex finds a single digit followed by a 3 digit set and adds a comma after the first sub match
  const amount = dollarAmount.includes('-')
    ? `-$${dollarAmount.split('-')[1]}`
    : `$${dollarAmount}`;
  return amount;
};

export const numberWithCommas = x => (x ?? '').toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',');

// Format a string to a safe slug for use in HTML element id attributes
export const formatSelectorSlug = string => {
  if (!string || typeof string !== 'string') {
    return '';
  }
  return (string.replace(/\s/g, '-').match(/-?[_a-zA-Z]+[_a-zA-Z0-9-]*/g, '') || [])
    .join('').toLowerCase();
};

// Truncate a string
export const truncate = (string, length = 12, lastLength = 0) => {
  if (!string || typeof string !== 'string') return '';
  if (lastLength) {
    return string.length <= length + lastLength
      ? string
      : `${string.slice(0, length - 1)}...${string.slice(string.length - lastLength)}`;
  }
  return string.length <= length ? string : `${string.slice(0, length - 2)}...`;
};

// Shorten multi-word names by capitalizing the first letter of each word
// ID Analytics ID Score will be IAIS
export const shortenName = (name, trunc = true, len = 10) => {
  if (name.length > len) {
    const wordList = name.split(' ');

    if (wordList.length > 1) {
      let shortName = '';

      for (let i = 0; i < wordList.length; i += 1) {
        shortName += wordList[i].charAt(0).toUpperCase();
      }
      return trunc ? truncate(shortName) : shortName;
    }

    return trunc ? truncate(wordList[0]) : wordList[0];
  }
  return name;
};

// Helper to check for specific capitalization formats
export const checkCapsFormat = (value, wordList) => {
  const stringValue = value ? value.split(' ') : value;
  return stringValue.map(val => (
    wordList.includes(val.toLowerCase()) ? val.toUpperCase() : val
  ))
    .join(' ');
};

export const splitOnNewlines = (string = '') => {
  let lines = {};
  if (string && typeof string === 'string') {
    lines = string.split('\n');
  }
  return Object.keys(lines)
    .map(index => (
      <p key={index} className={+index === lines.length - 1 ? '' : 'header-8'}>{lines[index]}</p>
    ));
};

// Stringifies and removes the leading zero of any number that is less than zero 0.123 -> '.123'
export const removeLeadingZero = value => {
  if (['number', 'string'].includes(typeof value)) {
    const numString = value.toString();
    if (numString.substring(0, 2) === '0.') {
      return numString.substring(1);
    }
    return numString;
  }
  return '';
};

export const formatBool = x => (x ? 'True' : 'False');

export const accountTypeIcons = {
  merchant: 'fal fa-store',
  deposit: 'fal fa-university',
  savings: 'fal fa-university',
  debit: 'fas fa-money-check',
  checking: 'fas fa-money-check',
  credit: 'fal fa-credit-card',
  generic: 'fal fa-usd-circle',
};

const BILLION = 1.0e+9;
const MILLION = 1.0e+6;
const NUM_DECIMAL = 4;

const isBigNumber = maybeBigNumber => Math.abs(Number(maybeBigNumber)) >= MILLION;

const isLongDecimal = maybeLongDecimal => {
  const numStr = maybeLongDecimal.toString();
  const splitStr = numStr.split('.');

  if (splitStr.length === 2) {
    return splitStr[1].length > NUM_DECIMAL;
  }
  return false;
};

const truncateBigNum = maybeBigNumber => {
  const isNegative = Number(maybeBigNumber) < 0;
  const maybeMinusSign = isNegative ? '-' : '';
  const absValue = Math.abs(Number(maybeBigNumber));

  if (absValue >= BILLION) {
    return `${maybeMinusSign}${truncateDecimals(absValue / BILLION)}B`;
  }

  if (absValue >= MILLION) {
    return `${maybeMinusSign}${truncateDecimals(absValue / MILLION)}M`;
  }

  return maybeBigNumber;
};

const truncateDecimals = decimalNum => {
  if (isLongDecimal(decimalNum)) {
    return Number(decimalNum).toFixed(NUM_DECIMAL);
  }

  return decimalNum;
};

export const truncateNumber = num => (isBigNumber(num)
  ? truncateBigNum(num) : truncateDecimals(num));

/**
 * Determine if a given string contains a URL: web or mailto
 * If the given input is not a string or is a numerical string, return false
 */
export const isLink = str => {
  const isNumber = Number.isNaN(Number(str)) === false;
  if (typeof str !== 'string' || isNumber) {
    return false;
  }

  // based on: https://uibakery.io/regex-library/url
  // The change was to acommodate links like "alloy.com"
  // The eslint disable is necessary because the regex statement is not parsed
  // like a regex statement
  // eslint-disable-next-line no-useless-escape
  const urlRegex = /^(https?:\/\/(?:www\.)?)?[-a-zA-Z0-9@:%._\+~#=]{1,256}\.[a-zA-Z0-9()]{1,6}\b(?:[-a-zA-Z0-9()@:%_\+.~#?&\/=]*)$/;

  return urlRegex.test(str);
};

/**
 * Returns an object with the appropriate url and display for a link.
 * The link may be an email link or may lack a protocol.
 * Adds "https" to urls that lack the protocol part of the link.
 * e.g. alloy.com -> https://alloy.com
 * Why https and not http? To be on the safe side.
 * Why searching for just "http" and not "https"? To cast a wider net.
 */
export const getValidLink = link => {
  if (link.includes('mailto')) {
    return { url: link, display: link.replace('mailto:', '') };
  }

  if (!link.includes('http')) {
    return { url: `https://${link}`, display: link };
  }

  return { url: link, display: link };
};


/**
 * Returns an array of object with all non empty string attr
 * For example:
 * we have [{foo: 'a', bar: '', baz: ''}, {foo: '', bar: 'b', baz: 'c'}]
 * return [{foo: 'a'}, {bar: 'b', baz: 'c'}]
 */
export const cleanEmptyValueAttr = listOfObj => {
  const listOfObjCleaned = listOfObj.reduce((acc, el) => {
    acc.push(Object.fromEntries(Object.entries(el).filter(([, v]) => v !== '')));
    return acc;
  }, []);
  return listOfObjCleaned;
};


/**
 * Returns a normalized string; trimmed original in lowercase, with spaces instead of underscores
 * Useful for comparing strings that come from different sources
 */
export const normalizeString = string => string.trim().toLocaleLowerCase().replace(/_/g, ' ');

/**
 * This function is especially useful for outcomes where we may have a special
 * character(e.g Fraud & Compliance, Fraud - Compliance, etc.). In several places on the front-end we
 * are stripping those special characters. This function will capitalize the outcomes and preserve special
 * characters
 */
export const capitalizeStringAndPreserveSpecialCharacters = string => {
  const normalizedString = normalizeString(string);

  return normalizedString.split(' ').reduce((acc, word) => {
    acc.push(word.charAt(0).toUpperCase() + word.slice(1));
    return acc;
  }, []).join(' ');
};

/**
 * @param {number} bytes
 */
export const bytesToSize = bytes => {
  const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB'];
  if (bytes === 0) return 'n/a';
  const i = parseInt(Math.floor(Math.log(bytes) / Math.log(1024)), 10);
  if (i === 0) return `${bytes} ${sizes[i]})`;
  return `${(bytes / (1024 ** i)).toFixed(1)} ${sizes[i]}`;
};

export const isEmpty = obj => !Object.entries(obj || {}).length && !obj?.length && !obj?.size;
